import { httpClient } from './http-client';

/**
 * Response shape for a Personal Access Token (without the raw token value).
 */
export interface PATResponse {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  last_used_at?: string;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
}

/**
 * Response returned when a new PAT is created. Includes the raw token
 * value that will only be shown once.
 */
export interface CreatePATResponse extends PATResponse {
  token: string; // Only returned on creation
}

/**
 * Request payload for creating a new Personal Access Token.
 */
export interface CreatePATRequest {
  name: string;
  scopes?: string[];
  expires_in?: number; // days
}

/**
 * Service for managing Personal Access Tokens.
 *
 * Communicates with the /api-tokens backend API endpoints.
 */
export class PATService {
  private static readonly BASE_URL = '/api-tokens';

  /**
   * List all Personal Access Tokens for the current user.
   *
   * Returns:
   *     Object containing tokens array and total count.
   */
  static async list(): Promise<{ tokens: PATResponse[]; total: number }> {
    const raw = await httpClient.get<any>(PATService.BASE_URL);
    const data = (raw as any).data || raw;
    return {
      tokens: data.tokens || [],
      total: data.total || 0,
    };
  }

  /**
   * Create a new Personal Access Token.
   *
   * Args:
   *     data (CreatePATRequest): The token creation payload.
   *
   * Returns:
   *     CreatePATResponse: The created token including the raw token value.
   */
  static async create(data: CreatePATRequest): Promise<CreatePATResponse> {
    const raw = await httpClient.post<any>(PATService.BASE_URL, data);
    return (raw as any).data || raw;
  }

  /**
   * Revoke (delete) a Personal Access Token.
   *
   * Args:
   *     id (string): The token ID to revoke.
   */
  static async revoke(id: string): Promise<void> {
    await httpClient.delete(`${PATService.BASE_URL}/${id}`);
  }
}
